import { User } from '../../types';

export abstract class ApiClient {
	protected baseURL: string;
	private token: string | null = null;
	private refreshToken: string | null = null;
	private user: User | null = null;
	private isRefreshing: boolean = false;
	private refreshPromise: Promise<string> | null = null;

	constructor(baseURL: string) {
		this.baseURL = baseURL;
		this.loadFromStorage();
		this.checkTokenExpiry();
	}

	protected async request<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
		// First attempt with current token
		let response = await fetch(`${this.baseURL}${endpoint}`, {
			...options,
			headers: {
				'Content-Type': 'application/json',
				...(this.token && { Authorization: `Bearer ${this.token}` }),
				...options.headers,
			},
		});

		// If 401 and we have a refresh token, try to refresh
		if (response.status === 401 && this.refreshToken) {
			try {
				const newToken = await this.handleTokenRefresh();

				// Retry the request with new token
				response = await fetch(`${this.baseURL}${endpoint}`, {
					...options,
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${newToken}`,
						...options.headers,
					},
				});
			} catch (refreshError) {
				// Refresh failed, clear auth and redirect to login
				this.clearAuth();
				window.location.href = '/login';
				throw new Error('Session expired. Please login again.');
			}
		} else if (response.status === 401) {
			// No refresh token available, clear auth
			this.clearAuth();
			window.location.href = '/login';
			throw new Error('Authentication failed');
		}

		if (!response.ok) {
			const error = await response.json().catch(() => ({
				message: `HTTP ${response.status}: ${response.statusText}`,
			}));
			throw new Error(error.message || error.error || 'API request failed');
		}

		return response.json();
	}

	/**
	 * Handle token refresh with de-duplication
	 */
	private async handleTokenRefresh(): Promise<string> {
		// If already refreshing, wait for that refresh to complete
		if (this.isRefreshing && this.refreshPromise) {
			return this.refreshPromise;
		}

		// Start new refresh
		this.isRefreshing = true;
		this.refreshPromise = this.performTokenRefresh();

		try {
			const newToken = await this.refreshPromise;
			return newToken;
		} finally {
			this.isRefreshing = false;
			this.refreshPromise = null;
		}
	}

	/**
	 * Perform the actual token refresh API call
	 */
	private async performTokenRefresh(): Promise<string> {
		const response = await fetch('/api/auth/refresh', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ refreshToken: this.refreshToken }),
		});

		if (!response.ok) {
			throw new Error('Token refresh failed');
		}

		const data = await response.json();

		if (data.success && data.tokens?.accessToken) {
			this.setToken(data.tokens.accessToken);
			return data.tokens.accessToken;
		}

		throw new Error('Invalid refresh response');
	}

	/**
	 * Check if refresh token has expired (7 days)
	 * Auto-logout if expired
	 */
	private checkTokenExpiry(): void {
		const refreshTokenExpiry = localStorage.getItem('refreshTokenExpiry');

		if (refreshTokenExpiry) {
			const expiryTime = parseInt(refreshTokenExpiry, 10);
			const now = Date.now();

			if (now > expiryTime) {
				// Refresh token expired, clear auth
				this.clearAuth();
				if (window.location.pathname !== '/login') {
					window.location.href = '/login';
				}
			}
		}

		// Set up periodic check every minute
		setInterval(() => {
			const refreshTokenExpiry = localStorage.getItem('refreshTokenExpiry');
			if (refreshTokenExpiry) {
				const expiryTime = parseInt(refreshTokenExpiry, 10);
				const now = Date.now();

				if (now > expiryTime) {
					this.clearAuth();
					if (window.location.pathname !== '/login') {
						window.location.href = '/login';
					}
				}
			}
		}, 60000); // Check every minute
	}

	protected setToken(token: string): void {
		this.token = token;
		localStorage.setItem('adminToken', token);
	}

	protected setRefreshToken(refreshToken: string): void {
		this.refreshToken = refreshToken;
		localStorage.setItem('adminRefreshToken', refreshToken);

		// Set expiry time for refresh token (7 days from now)
		const expiryTime = Date.now() + 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
		localStorage.setItem('refreshTokenExpiry', expiryTime.toString());
	}

	protected setUser(user: User): void {
		this.user = user;
		localStorage.setItem('adminUser', JSON.stringify(user));
	}

	protected getUser(): User | null {
		return this.user;
	}

	protected getToken(): string | null {
		return this.token;
	}

	protected hasValidToken(): boolean {
		return !!this.token;
	}

	protected clearAuth(): void {
		this.token = null;
		this.refreshToken = null;
		this.user = null;
		localStorage.removeItem('adminToken');
		localStorage.removeItem('adminRefreshToken');
		localStorage.removeItem('refreshTokenExpiry');
		localStorage.removeItem('adminUser');
	}

	private loadFromStorage(): void {
		this.token = localStorage.getItem('adminToken');
		this.refreshToken = localStorage.getItem('adminRefreshToken');
		const storedUser = localStorage.getItem('adminUser');
		if (storedUser) {
			try {
				this.user = JSON.parse(storedUser);
			} catch (e) {
				console.warn('Failed to parse stored user data');
			}
		}
	}
}
